import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../constants/api';

const APP_STORE_URL = 'https://apps.apple.com/app/spotto/id6740039498';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=pl.spotto.mobile';
const APP_SCHEME = 'spottospace';
const APP_ID = '6740039498';

type Product = {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency?: string;
  validityDays?: number;
  entriesCount?: number;
  imageUrl?: string;
  businessProfile?: {
    id: string;
    displayName: string;
  };
};

// Helper to update or create meta tag
const setMetaTag = (property: string, content: string, isName = false) => {
  const attr = isName ? 'name' : 'property';
  let element = document.querySelector(`meta[${attr}="${property}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, property);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

export const ProductDeepLink: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const { t } = useTranslation();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);

  // Detect platform
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  // Update meta tags when product data is loaded
  useEffect(() => {
    if (product) {
      document.title = `${product.name} - Spotto`;

      // Open Graph meta tags
      setMetaTag('og:title', product.name);
      setMetaTag('og:description', product.description || t('product.ogDescription'));
      setMetaTag('og:image', product.imageUrl || 'https://spotto.pl/icon.png');
      setMetaTag('og:url', `https://spotto.pl/product/${productId}`);
      setMetaTag('og:type', 'website');

      // Twitter Card meta tags
      setMetaTag('twitter:card', 'summary_large_image', true);
      setMetaTag('twitter:title', product.name, true);
      setMetaTag('twitter:description', product.description || t('product.ogDescription'), true);
      setMetaTag('twitter:image', product.imageUrl || 'https://spotto.pl/icon.png', true);

      // iOS Smart App Banner
      setMetaTag('apple-itunes-app', `app-id=${APP_ID}, app-argument=${APP_SCHEME}://product/${productId}`, true);
    }

    return () => {
      // Cleanup: Reset title on unmount
      document.title = 'Spotto - Sport Facility Scheduler';
    };
  }, [product, productId, t]);

  useEffect(() => {
    fetchProduct();

    // Try to open app on mobile
    if (isMobile && !attempted) {
      tryOpenApp();
    }
  }, [productId]);

  const fetchProduct = async () => {
    if (!productId) return;

    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/api/products/${productId}`);
      if (!res.ok) {
        throw new Error('Product not found');
      }
      const data = await res.json();
      setProduct(data);
    } catch (err) {
      console.error('Failed to fetch product:', err);
      setError(t('product.productNotFound'));
    } finally {
      setLoading(false);
    }
  };

  const tryOpenApp = () => {
    setAttempted(true);
    const appUrl = `${APP_SCHEME}://product/${productId}`;

    // Try to open the app
    window.location.href = appUrl;

    // Fallback to store after timeout (if app didn't open)
    setTimeout(() => {
      window.location.href = isIOS ? APP_STORE_URL : PLAY_STORE_URL;
    }, 2500);
  };

  const formatPrice = (price: number, currency?: string) => {
    return `${price.toFixed(2)} ${currency || 'PLN'}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-surface-card to-surface-app flex flex-col items-center justify-center p-6">
      {/* Logo */}
      <div className="mb-8">
        <img src="/icon.png" alt="Spotto" className="w-20 h-20 rounded-2xl shadow-lg" />
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-ink-secondary text-center">
          <div className="animate-spin w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>{t('product.loading')}</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">😕</div>
          <p className="text-brand-warm-deep mb-4">{error}</p>
        </div>
      )}

      {/* Product Info Card */}
      {product && !loading && (
        <div className="bg-surface-app-elevated rounded-2xl p-6 max-w-md w-full shadow-xl border border-border-strong mb-8">
          <div className="flex items-center gap-4 mb-4">
            {product.imageUrl ? (
              <img
                src={product.imageUrl}
                alt={product.name}
                className="w-16 h-16 rounded-xl object-cover"
              />
            ) : (
              <div className="w-16 h-16 bg-brand-primary rounded-xl flex items-center justify-center text-2xl">
                🎫
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-white">{product.name}</h1>
              {product.businessProfile && (
                <p className="text-ink-secondary text-sm">{product.businessProfile.displayName}</p>
              )}
            </div>
          </div>

          {product.description && (
            <p className="text-ink-primary text-sm mb-4">{product.description}</p>
          )}

          {/* Product Details */}
          <div className="space-y-2 pt-4 border-t border-border-strong">
            {/* Price */}
            <div className="flex items-center justify-between">
              <span className="text-ink-secondary text-sm">{t('product.price')}</span>
              <span className="text-white font-semibold">{formatPrice(product.price, product.currency)}</span>
            </div>

            {/* Validity */}
            {product.validityDays && (
              <div className="flex items-center justify-between">
                <span className="text-ink-secondary text-sm">{t('product.validity')}</span>
                <span className="text-white">{product.validityDays} {t('product.days')}</span>
              </div>
            )}

            {/* Entries */}
            {product.entriesCount && (
              <div className="flex items-center justify-between">
                <span className="text-ink-secondary text-sm">{t('product.entries')}</span>
                <span className="text-white">{product.entriesCount}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Download Section */}
      <div className="text-center max-w-md">
        <h2 className="text-2xl font-bold text-white mb-2">{t('product.downloadTitle')}</h2>
        <p className="text-ink-secondary mb-6">{t('product.downloadDescription')}</p>

        {/* Store Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-black hover:bg-black/80 text-white px-6 py-3 rounded-xl transition-colors border border-border-strong"
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            <div className="text-left">
              <div className="text-xs text-ink-secondary">{t('bookBusiness.downloadOn')}</div>
              <div className="text-lg font-semibold">App Store</div>
            </div>
          </a>

          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-black hover:bg-black/80 text-white px-6 py-3 rounded-xl transition-colors border border-border-strong"
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
            </svg>
            <div className="text-left">
              <div className="text-xs text-ink-secondary">{t('bookBusiness.getItOn')}</div>
              <div className="text-lg font-semibold">Google Play</div>
            </div>
          </a>
        </div>

        {/* Open in App Button (Mobile only) */}
        {isMobile && (
          <button
            onClick={tryOpenApp}
            className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white font-semibold py-3 px-6 rounded-xl transition-colors"
          >
            {t('product.openInApp')}
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="mt-12 text-center text-ink-muted text-sm">
        <p>© {new Date().getFullYear()} Spotto. {t('bookBusiness.allRightsReserved')}</p>
      </div>
    </div>
  );
};
